import { trace } from '@opentelemetry/api'
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
  WebTracerProvider
} from '@opentelemetry/sdk-trace-web'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'
import type { Metric } from 'web-vitals'
import { recordErrorSpan } from 'utils/telemetry/errors'

const SERVICE_NAME = 'arvore-frontend'
const OTLP_TRACES_URL = 'https://otlp.arvore.com.br/v1/traces'
const SAMPLING_RATIO = 0.2
const PROPAGATE_TRACE_HEADER_CORS_URLS = [/^https:\/\/livros\.arvore\.com\.br/]
const IGNORE_URLS = [/^https:\/\/otlp\.arvore\.com\.br/]

let initialized = false

const reportWebVital = (metric: Metric): void => {
  const span = trace.getTracer(SERVICE_NAME).startSpan('web-vitals')
  span.setAttribute('web_vital.name', metric.name)
  span.setAttribute('web_vital.value', metric.value)
  span.setAttribute('web_vital.delta', metric.delta)
  span.setAttribute('web_vital.id', metric.id)
  span.setAttribute('web_vital.rating', metric.rating)
  span.setAttribute('web_vital.navigation_type', metric.navigationType)
  span.end()
}

const registerWebVitals = (): void => {
  onLCP(reportWebVital)
  onCLS(reportWebVital)
  onINP(reportWebVital)
  onTTFB(reportWebVital)
  onFCP(reportWebVital)
}

const registerGlobalErrorHandlers = (): void => {
  window.addEventListener('error', event => {
    recordErrorSpan(event.error ?? event.message, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })

  window.addEventListener('unhandledrejection', event => {
    recordErrorSpan(event.reason, { source: 'unhandledrejection' })
  })
}

export const initTelemetry = (): void => {
  if (initialized || process.env.NODE_ENV !== 'production') return
  initialized = true

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({ [ATTR_SERVICE_NAME]: SERVICE_NAME }),
    sampler: new TraceIdRatioBasedSampler(SAMPLING_RATIO),
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: OTLP_TRACES_URL }))
    ]
  })

  provider.register()

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: PROPAGATE_TRACE_HEADER_CORS_URLS,
        ignoreUrls: IGNORE_URLS
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: PROPAGATE_TRACE_HEADER_CORS_URLS,
        ignoreUrls: IGNORE_URLS
      }),
      new UserInteractionInstrumentation()
    ]
  })

  registerWebVitals()
  registerGlobalErrorHandlers()
}
